<script setup lang="ts">
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import type { PlanBannerConfig } from '~/composables/PlanBanners'
  import QuoteCardPolicyBadges from '~/components/quote/card/QuoteCardPolicyBadges.vue'

  const props = defineProps<{
    id: string
    quote: QuotedPlanVm
    banner?: Partial<PlanBannerConfig & { text: string }> | null | undefined
    stacked?: Boolean
  }>()

  const { getId } = Id.use(props.id)
  const { getComponentContent, getTooltipContent, hydratePlaceholders } = Cms.use()
  const { banner } = await PlanBanners.use(props.quote.medicareId)
  const { content: starRatingTooltip } = await getTooltipContent('MedicareStarRating')

  const usedBanner = computed(() => props.banner ?? banner.value)

  const verticalOrMobileStyles = computed(() => {
    return {
      banner: props.stacked
        ? 'left-1/2 flex -translate-x-1/2'
        : 'left-1/2 flex -translate-x-1/2 md:left-3 md:translate-x-0',
      header: props.stacked ? 'flex-col' : 'flex-col md:flex-row',
      planNameHorizontal: props.stacked ? 'hidden' : 'hidden md:flex',
      planNameVertical: props.stacked ? '' : 'md:hidden'
    }
  })
</script>

<template>
  <section class="relative">
    <aside
      v-if="!!usedBanner || !!$slots.banner"
      class="header-banner"
      :class="[`${usedBanner?.color}`, verticalOrMobileStyles.banner]"
    >
      <HpIcon v-if="!!usedBanner?.icon" :name="usedBanner.icon" class="size-5" />
      {{ usedBanner?.text }}
    </aside>

    <div
      class="flex w-full items-center justify-between gap-5"
      :class="verticalOrMobileStyles.header"
    >
      <figure class="w-full">
        <PxCarrierLogo
          class="carrier-logo"
          :class="[`${quote.details.carrierKey}`, stacked ? 'mx-auto' : 'mx-auto md:mx-0']"
          :logo-key="quote.details.logoKey!"
          :plan-year="quote.details.year!"
          :logo-alt="`${quote.details.logoKey} carrier logo`"
        />
        <figcaption
          class="text-balance text-center leading-tight"
          :class="verticalOrMobileStyles.planNameVertical"
        >
          {{ quote.name }}
        </figcaption>
      </figure>

      <div
        class="mb-2 flex flex-wrap items-center justify-center gap-2 md:flex-nowrap md:justify-end"
      >
        <SitePartBGivebackBadge
          :amount="quote.recommendation.partBReduction ?? 0"
          class="w-full text-center"
        />
        <QuoteBadge :text="quote.details.year!.toString()" variant="white" />
        <QuoteCardPolicyBadges :id="getId()" :quote="quote" />
        <PxStarRating
          :star-rating="quote.details.starRating"
          :tooltip="{
            id: getId(`star-rating-tooltip`),
            title: starRatingTooltip.title,
            body: hydratePlaceholders(starRatingTooltip.body, { planYear: quote.details.year })
          }"
        />
      </div>
    </div>

    <h2
      class="text-center text-lg font-normal leading-tight md:text-left"
      :class="verticalOrMobileStyles.planNameHorizontal"
    >
      {{ quote.name }}
    </h2>
  </section>
</template>

<style scoped>
  :deep(.carrier-logo) {
    @apply object-fill object-left;
  }

  .carrier-logo.highmark {
    @apply h-full;
  }

  .header-banner {
    @apply absolute -top-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-t-[1.25rem] bg-sky-800 px-5 py-0.5 text-base font-semibold capitalize text-white;

    &.green {
      @apply bg-success;
    }
  }
</style>
